/**
 * JOFooterEnhancer — Adds Jardins d'Éole-specific sections to the bottom of the cockpit footer.
 * Renders directly inside PublicLayout, after the cockpit Footer. Universe-aware.
 *
 * NON-CORE FILE — additive customization.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sprout, TreePine, BookOpen, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { useUniverse } from "@/lib/jardins-ole/useUniverse";
import { useSettings } from "@/lib/useSettings";
import useUniversePhone from "@/lib/jardins-ole/useUniversePhone";
import { api } from "@/lib/api";

export default function JOFooterEnhancer() {
  const { enabled, configs } = useUniverse();
  const { settings: s } = useSettings();
  const phone = useUniversePhone();
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    api.get("/services").then((r) => {
      if (!alive) return;
      setServices((r.data || []).filter((sv) => sv.category === "paysagiste").slice(0, 8));
    }).catch(() => {});
    return () => { alive = false; };
  }, [enabled]);

  if (!enabled) return null;

  const jardinerie = configs?.jardinerie;
  const paysagiste = configs?.paysagiste;

  return (
    <section className="jo-footer-enhancer" data-testid="jo-footer-enhancer">
      <div className="jo-footer-rich">
        <div className="jo-footer-grid">
          {/* COL 1 — Brand */}
          <div className="jo-footer-brand">
            <div className="jo-footer-tag">— Depuis 1993</div>
            <h3 className="jo-footer-title">
              Jardinerie<br />&amp; Paysagiste<br /><em>à Istres</em>
            </h3>
            <p className="jo-footer-desc">
              Un havre de paix sur 3 000 m² au cœur des Bouches-du-Rhône. Un lieu où il fait bon flâner,
              découvrir et trouver la plante ou le projet qui vous correspond.
            </p>
            <div className="jo-footer-contact">
              {phone && (
                <a href={`tel:${phone.replace(/\s/g, "")}`} className="jo-footer-link">
                  <Phone className="w-3.5 h-3.5" /> {phone}
                </a>
              )}
              {s?.email && (
                <a href={`mailto:${s.email}`} className="jo-footer-link">
                  <Mail className="w-3.5 h-3.5" /> {s.email}
                </a>
              )}
              {s?.address_public && (
                <span className="jo-footer-link">
                  <MapPin className="w-3.5 h-3.5" /> {s.address_public}
                </span>
              )}
            </div>
          </div>

          {/* COL 2 — Jardinerie */}
          <div>
            <div className="jo-footer-h">
              <span className="jo-footer-icon" style={{ background: "rgba(201, 158, 48, 0.15)", color: "#E8C97A" }}>
                <Sprout className="w-3.5 h-3.5" strokeWidth={2.4} />
              </span>
              <span>Jardinerie</span>
            </div>
            <ul className="jo-footer-list">
              <li><Link to="/shop">Boutique en ligne</Link></li>
              <li><Link to="/shop?cat=plantes-mediterraneennes">Plantes méditerranéennes</Link></li>
              <li><Link to="/shop?cat=arbres-fruitiers">Arbres fruitiers</Link></li>
              <li><Link to="/shop?cat=aromatiques">Plantes aromatiques</Link></li>
              <li><Link to="/shop?cat=poteries">Poteries &amp; déco</Link></li>
              <li><Link to="/livraison-retours">Livraison &amp; Click&nbsp;&amp;&nbsp;Collect</Link></li>
            </ul>
          </div>

          {/* COL 3 — Paysagiste */}
          <div>
            <div className="jo-footer-h">
              <span className="jo-footer-icon" style={{ background: "rgba(58, 90, 130, 0.15)", color: "#9CC0E8" }}>
                <TreePine className="w-3.5 h-3.5" strokeWidth={2.4} />
              </span>
              <span>Paysagiste</span>
            </div>
            <ul className="jo-footer-list">
              {services.length > 0 ? services.map((sv) => (
                <li key={sv.slug}>
                  <Link to={`/services/${sv.slug}`}>{sv.title}</Link>
                </li>
              )) : (
                <>
                  <li><Link to="/services/paysagiste">Création de jardin</Link></li>
                  <li><Link to="/services/paysagiste">Entretien régulier</Link></li>
                  <li><Link to="/services/paysagiste">Conception 3D</Link></li>
                </>
              )}
            </ul>
          </div>

          {/* COL 4 — Apprendre */}
          <div>
            <div className="jo-footer-h">
              <span className="jo-footer-icon" style={{ background: "rgba(143, 168, 118, 0.18)", color: "#C5DDA9" }}>
                <BookOpen className="w-3.5 h-3.5" strokeWidth={2.4} />
              </span>
              <span>Apprendre</span>
            </div>
            <ul className="jo-footer-list">
              <li><Link to="/blog">Tutos &amp; conseils</Link></li>
              <li><Link to="/blog?cat=plantation">Comment planter</Link></li>
              <li><Link to="/blog?cat=taille">Taille &amp; entretien</Link></li>
              <li><Link to="/blog?cat=potager">Calendrier du potager</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom universe quick switcher */}
        <div className="jo-footer-universe-bar">
          <div className="jo-footer-universe-card" data-color="gold">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#E8C97A] mb-1.5">{jardinerie?.tagline || "Jardinerie"}</div>
            <div className="font-display text-2xl font-semibold mb-2 leading-tight" style={{ color: "#FAF7EE" }}>
              Visitez nos 3 000 m² de pépinière
            </div>
            <Link to="/shop" className="jo-footer-cta">
              Découvrir la boutique <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="jo-footer-universe-card" data-color="blue">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#9CC0E8] mb-1.5">{paysagiste?.tagline || "Paysagiste"}</div>
            <div className="font-display text-2xl font-semibold mb-2 leading-tight" style={{ color: "#FAF7EE" }}>
              Concevons votre jardin sur-mesure
            </div>
            <Link to="/devis" className="jo-footer-cta">
              Demander un devis <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
